/**
 * @fileoverview Dollar Liquidity Observatory Dashboard
 * Full-featured observatory page with stress gauge, TradingView widgets,
 * market snapshot, and methodology modal.
 * 
 * Phase 1.1: Enhanced Visual Design
 * - Improved chart readability with larger heights
 * - Glassmorphic card containers
 * - Commentary stub sections for future AI insights
 * - Tighter spacing and visual hierarchy
 * 
 * @module observatories/dollar_liquidity/DollarLiquidityDashboard
 */

import { el } from "../../shared/components/simpleRender.js";

// ============================================
// CLEANUP REGISTRY
// ============================================

/**
 * Tracks active intervals for cleanup on page navigation.
 * @type {Set<number>}
 */
const activeIntervals = new Set();

/**
 * Tracks active timeouts for cleanup on page navigation.
 * @type {Set<number>}
 */
const activeTimeouts = new Set();

/**
 * Registers an interval for later cleanup.
 * @param {number} intervalId - The interval ID from setInterval
 * @returns {number} The same interval ID
 */
function registerInterval(intervalId) {
  activeIntervals.add(intervalId);
  return intervalId;
}

/**
 * Registers a timeout for later cleanup.
 * @param {number} timeoutId - The timeout ID from setTimeout
 * @returns {number} The same timeout ID
 */
function registerTimeout(timeoutId) {
  activeTimeouts.add(timeoutId);
  return timeoutId;
}

/**
 * Cleans up all registered intervals and timeouts.
 * Call this when navigating away from the observatory.
 */
export function cleanupDollarLiquidityDashboard() {
  console.log(`[Observatory] Cleaning up ${activeIntervals.size} intervals and ${activeTimeouts.size} timeouts`);
  
  activeIntervals.forEach(id => clearInterval(id));
  activeIntervals.clear();
  
  activeTimeouts.forEach(id => clearTimeout(id));
  activeTimeouts.clear();
  
  // Reset body overflow in case modal was open
  document.body.style.overflow = "";
}

// ============================================
// CONFIGURATION
// ============================================

/**
 * Observatory sub-navigation tabs
 */
const OBS_NAV_TABS = [
  { id: "overview", label: "Overview", active: true },
  { id: "usd-core", label: "USD Core", active: false },
  { id: "fx-stress", label: "FX Stress", active: false },
  { id: "risk-credit", label: "Risk & Credit", active: false },
  { id: "methodology", label: "Methodology", active: false },
];

/**
 * TradingView ticker symbols for the tape
 */
const TICKER_SYMBOLS = [
  { proName: "TVC:DXY", title: "DXY" },
  { proName: "AMEX:UUP", title: "UUP" },
  { proName: "AMEX:USDU", title: "USDU" },
  { proName: "FX:USDJPY", title: "USD/JPY" },
  { proName: "FX:EURUSD", title: "EUR/USD" },
  { proName: "FX:USDCNH", title: "USD/CNH" },
  { proName: "AMEX:HYG", title: "HYG" },
  { proName: "TVC:GOLD", title: "GOLD" },
  { proName: "CBOE:VIX", title: "VIX" },
  { proName: "TVC:US10Y", title: "US 10Y" },
];

/**
 * Market Overview widget tab configuration
 */
const MARKET_OVERVIEW_TABS = [
  {
    title: "USD Core",
    symbols: [
      { s: "TVC:DXY", d: "Dollar Index" },
      { s: "AMEX:UUP", d: "UUP ETF" },
      { s: "AMEX:USDU", d: "USDU Broad" },
    ],
  },
  {
    title: "FX Stress",
    symbols: [
      { s: "FX:USDJPY", d: "USD/JPY" },
      { s: "FX:EURUSD", d: "EUR/USD" },
      { s: "FX:USDCNH", d: "USD/CNH" },
    ],
  },
  {
    title: "Risk & Credit",
    symbols: [
      { s: "AMEX:HYG", d: "High Yield" },
      { s: "TVC:GOLD", d: "Gold" },
      { s: "CBOE:VIX", d: "VIX" },
      { s: "TVC:US10Y", d: "US 10Y Yield" },
    ],
  },
];

// ============================================
// CHART CONFIGURATION - Enhanced for readability
// ============================================

/**
 * Chart heights optimized for readability
 */
const CHART_HEIGHTS = {
  hero: 550,      // Main featured chart
  standard: 480,  // Regular chart
  compact: 420,   // Secondary charts
  mini: 320       // Treasury yields, small charts
};

/**
 * Enhanced TradingView chart config for better readability
 */
const CHART_STYLE_CONFIG = {
  backgroundColor: "rgba(10, 14, 17, 1)",
  gridColor: "rgba(151, 184, 160, 0.04)",
  textColor: "rgba(152, 166, 173, 1)",
  candleUpColor: "#97B8A0",
  candleDownColor: "#B2332A",
  borderUpColor: "#97B8A0",
  borderDownColor: "#B2332A",
  wickUpColor: "#97B8A0",
  wickDownColor: "#B2332A"
};

// ============================================
// STRESS GAUGE LOGIC
// ============================================

/**
 * Mock stress data for demonstration
 */
function generateDemoData() {
  const baseStress = 45 + Math.random() * 30;
  const dxy = 30 + Math.random() * 50;
  const usdjpy = 25 + Math.random() * 55;
  const vix = 20 + Math.random() * 60;
  const hyg = 35 + Math.random() * 45;
  
  const composite = (dxy * 0.30) + (usdjpy * 0.25) + (vix * 0.25) + (hyg * 0.20);
  
  return {
    score: Math.round(Math.min(100, Math.max(0, composite))),
    components: {
      dxy: Math.round(dxy),
      usdjpy: Math.round(usdjpy),
      vix: Math.round(vix),
      hyg: Math.round(hyg)
    }
  };
}

/**
 * Get stress level classification
 */
function getStressLevel(score) {
  if (score < 25) return { label: "Low Stress", class: "status-low" };
  if (score < 45) return { label: "Moderate", class: "status-moderate" };
  if (score < 65) return { label: "Elevated", class: "status-elevated" };
  if (score < 80) return { label: "High Stress", class: "status-high" };
  return { label: "Extreme", class: "status-extreme" };
}

/**
 * Get component stress class
 */
function getComponentClass(value) {
  if (value < 30) return "stress-low";
  if (value < 50) return "stress-moderate";
  if (value < 70) return "stress-elevated";
  return "stress-high";
}

// ============================================
// TRADINGVIEW WIDGET LOADERS
// ============================================

/**
 * Loads TradingView ticker tape widget
 */
function loadTickerTape(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;
  
  container.innerHTML = '';
  
  const widgetContainer = document.createElement('div');
  widgetContainer.className = 'tradingview-widget-container';
  widgetContainer.innerHTML = '<div class="tradingview-widget-container__widget"></div>';
  container.appendChild(widgetContainer);
  
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-ticker-tape.js';
  script.async = true;
  script.innerHTML = JSON.stringify({
    symbols: TICKER_SYMBOLS,
    showSymbolLogo: false,
    isTransparent: true,
    displayMode: "adaptive",
    colorTheme: "dark",
    locale: "en"
  });
  
  widgetContainer.appendChild(script);
}

/**
 * Loads TradingView market overview widget
 */
function loadMarketOverview(containerId) {
  const container = document.getElementById(containerId);
  if (!container) return;
  
  container.innerHTML = '';
  
  const widgetContainer = document.createElement('div');
  widgetContainer.className = 'tradingview-widget-container';
  widgetContainer.style.cssText = 'height:100%;width:100%';
  widgetContainer.innerHTML = '<div class="tradingview-widget-container__widget" style="height:calc(100% - 32px);width:100%"></div>';
  container.appendChild(widgetContainer);
  
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-market-overview.js';
  script.async = true;
  script.innerHTML = JSON.stringify({
    colorTheme: "dark",
    dateRange: "1D",
    showChart: true,
    locale: "en",
    width: "100%",
    height: "100%",
    largeChartUrl: "",
    isTransparent: true,
    showSymbolLogo: false,
    showFloatingTooltip: true,
    plotLineColorGrowing: "rgba(151, 184, 160, 1)",
    plotLineColorFalling: "rgba(178, 51, 42, 1)",
    gridLineColor: "rgba(240, 243, 250, 0)",
    scaleFontColor: "rgba(152, 166, 173, 1)",
    belowLineFillColorGrowing: "rgba(151, 184, 160, 0.12)",
    belowLineFillColorFalling: "rgba(178, 51, 42, 0.12)",
    belowLineFillColorGrowingBottom: "rgba(151, 184, 160, 0)",
    belowLineFillColorFallingBottom: "rgba(178, 51, 42, 0)",
    symbolActiveColor: "rgba(151, 184, 160, 0.12)",
    tabs: MARKET_OVERVIEW_TABS.map(tab => ({
      ...tab,
      originalTitle: tab.title
    }))
  });
  
  widgetContainer.appendChild(script);
}

/**
 * Loads TradingView advanced chart widget with enhanced styling
 */
function loadAdvancedChart(containerId, symbol, height = CHART_HEIGHTS.standard) {
  const container = document.getElementById(containerId);
  if (!container) return;
  
  container.innerHTML = '';
  
  const widgetContainer = document.createElement('div');
  widgetContainer.className = 'tradingview-widget-container';
  widgetContainer.style.cssText = `height:${height}px;width:100%`;
  widgetContainer.innerHTML = '<div class="tradingview-widget-container__widget" style="height:100%;width:100%"></div>';
  container.appendChild(widgetContainer);
  
  const script = document.createElement('script');
  script.type = 'text/javascript';
  script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-advanced-chart.js';
  script.async = true;
  script.innerHTML = JSON.stringify({
    autosize: true,
    symbol: symbol,
    interval: "D",
    timezone: "America/New_York",
    theme: "dark",
    style: "1",
    locale: "en",
    backgroundColor: CHART_STYLE_CONFIG.backgroundColor,
    gridColor: CHART_STYLE_CONFIG.gridColor,
    withdateranges: true,
    hide_side_toolbar: false,
    allow_symbol_change: false,
    save_image: false,
    calendar: false,
    hide_volume: false,
    support_host: "https://www.tradingview.com"
  });
  
  widgetContainer.appendChild(script);
}

// ============================================
// RENDER FUNCTIONS
// ============================================

/**
 * Renders the observatory header with logo, market status, and nav
 * @param {string} activeTab - ID of the currently active tab
 */
function renderObservatoryHeader(activeTab = "overview") {
  return el("header", { className: "obs-header" }, [
    el("div", { className: "obs-header-inner" }, [
      el("div", { className: "obs-header-left" }, [
        el("a", { href: "#/observatories/dollar_liquidity", className: "obs-logo" }, [
          el("div", { className: "logo-dot" }, []),
          el("span", { className: "logo-text" }, ["Dollar Liquidity Observatory"])
        ]),
        el("div", { className: "market-status", id: "market-status" }, [
          el("span", { className: "status-dot" }, []),
          el("span", { className: "status-text" }, ["Loading..."])
        ])
      ]),
      el("nav", { className: "obs-nav" }, 
        OBS_NAV_TABS.map(tab => 
          el("a", { 
            href: `#/observatories/dollar_liquidity${tab.id === 'overview' ? '' : '/' + tab.id}`,
            className: `obs-nav-link ${tab.id === activeTab ? "active" : ""}`,
            "data-tab": tab.id
          }, [tab.label])
        )
      )
    ])
  ]);
}

/**
 * Renders the ticker tape container
 */
function renderTickerTape() {
  return el("div", { className: "ticker-tape-container", id: "ticker-tape" }, []);
}

/**
 * Renders the stress gauge SVG
 */
function renderStressGaugeSVG() {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("class", "gauge-svg");
  svg.setAttribute("viewBox", "0 0 200 120");
  
  svg.innerHTML = `
    <defs>
      <linearGradient id="gauge-gradient" x1="0%" y1="0%" x2="100%" y2="0%">
        <stop offset="0%" style="stop-color:#97B8A0" />
        <stop offset="35%" style="stop-color:#D4A85F" />
        <stop offset="65%" style="stop-color:#f97316" />
        <stop offset="100%" style="stop-color:#B2332A" />
      </linearGradient>
    </defs>
    <path class="gauge-bg" d="M 20 100 A 80 80 0 0 1 180 100" />
    <path class="gauge-fill" d="M 20 100 A 80 80 0 0 1 180 100" />
    <g class="gauge-ticks">
      <line x1="20" y1="100" x2="28" y2="100" />
      <line x1="26" y1="68" x2="33" y2="72" />
      <line x1="46" y1="42" x2="52" y2="48" />
      <line x1="76" y1="24" x2="80" y2="32" />
      <line x1="100" y1="20" x2="100" y2="28" />
      <line x1="124" y1="24" x2="120" y2="32" />
      <line x1="154" y1="42" x2="148" y2="48" />
      <line x1="174" y1="68" x2="167" y2="72" />
      <line x1="180" y1="100" x2="172" y2="100" />
    </g>
    <g class="gauge-needle" id="gauge-needle">
      <circle cx="100" cy="100" r="6" class="needle-cap" />
      <path d="M 100 100 L 97 95 L 100 30 L 103 95 Z" class="needle-pointer" />
    </g>
  `;
  
  return svg;
}

/**
 * Renders the Dollar Stress Index card
 */
function renderStressGaugeCard() {
  const card = el("div", { className: "widget-card stress-gauge-card" }, [
    el("div", { className: "gauge-header" }, [
      el("h2", { className: "widget-title" }, ["Dollar Stress Index"]),
      el("span", { className: "demo-badge" }, ["DEMO"])
    ]),
    el("p", { className: "widget-caption" }, [
      "Proprietary multi-factor composite tracking USD funding stress across FX, rates, and credit regimes."
    ]),
    el("div", { className: "gauge-container" }, [
      el("div", { className: "gauge-score" }, [
        el("span", { className: "score-value", id: "stress-score" }, ["--"]),
        el("span", { className: "score-label" }, ["/ 100"])
      ]),
      el("div", { className: "gauge-status", id: "stress-status" }, ["Calculating..."])
    ]),
    el("div", { className: "gauge-components" }, [
      el("div", { className: "component" }, [
        el("span", { className: "component-label" }, ["DXY Mom"]),
        el("div", { className: "component-bar" }, [
          el("div", { className: "component-fill", id: "comp-dxy" }, [])
        ]),
        el("span", { className: "component-value", id: "comp-dxy-val" }, ["--"])
      ]),
      el("div", { className: "component" }, [
        el("span", { className: "component-label" }, ["JPY Carry"]),
        el("div", { className: "component-bar" }, [
          el("div", { className: "component-fill", id: "comp-usdjpy" }, [])
        ]),
        el("span", { className: "component-value", id: "comp-usdjpy-val" }, ["--"])
      ]),
      el("div", { className: "component" }, [
        el("span", { className: "component-label" }, ["Vol Regime"]),
        el("div", { className: "component-bar" }, [
          el("div", { className: "component-fill", id: "comp-vix" }, [])
        ]),
        el("span", { className: "component-value", id: "comp-vix-val" }, ["--"])
      ]),
      el("div", { className: "component" }, [
        el("span", { className: "component-label" }, ["Credit Z"]),
        el("div", { className: "component-bar" }, [
          el("div", { className: "component-fill", id: "comp-hyg" }, [])
        ]),
        el("span", { className: "component-value", id: "comp-hyg-val" }, ["--"])
      ])
    ]),
    el("div", { className: "gauge-methodology" }, [
      el("button", { className: "methodology-btn", id: "methodology-btn" }, [
        el("span", { className: "btn-icon" }, ["◆"]),
        el("span", {}, ["View Methodology"])
      ])
    ])
  ]);
  
  return card;
}

/**
 * Renders the hero text section
 */
function renderHeroText() {
  return el("div", { className: "hero-text" }, [
    el("h1", { className: "obs-main-title" }, ["Global USD Liquidity Monitor"]),
    el("p", {}, [
      "This dashboard tracks the ",
      el("strong", {}, ["U.S. dollar"]),
      ", key FX pairs, and risk proxies that signal when global demand for dollar liquidity surges — pulling capital into USD assets and creating stress in international funding markets."
    ]),
    el("p", { style: "margin-top: 1rem;" }, [
      el("strong", {}, ["Panels:"]),
      " USD Core (DXY, UUP, USDU) · FX Stress (JPY, EUR, CNH) · Risk & Credit (HYG, Gold, VIX, Yields)"
    ])
  ]);
}

/**
 * Renders the market snapshot section with TradingView widget
 */
function renderMarketSnapshot() {
  return el("section", { className: "section" }, [
    el("div", { className: "widget-card market-snapshot-card" }, [
      el("h2", { className: "widget-title" }, ["Market Snapshot"]),
      el("p", { className: "widget-caption" }, [
        "Live overview of key dollar and risk indicators. Click through to dedicated pages for full charts."
      ]),
      el("div", { 
        className: "market-overview-widget", 
        id: "market-overview",
        style: "height: 450px; min-height: 400px;"
      }, [
        el("div", { className: "widget-loading" }, [
          el("span", { className: "loading-spinner" }, []),
          el("span", {}, ["Loading market data..."])
        ])
      ])
    ])
  ]);
}

/**
 * Renders the dashboard panels section
 */
function renderDashboardPanels() {
  return el("section", { className: "section" }, [
    el("div", { className: "section-header" }, [
      el("h2", {}, ["Dashboard Panels"]),
      el("p", {}, [
        "Each panel focuses on a specific dimension of dollar liquidity dynamics. Use the navigation above to explore detailed charts and analysis."
      ])
    ]),
    el("div", { className: "three-col-grid" }, [
      el("a", { href: "#/observatories/dollar_liquidity/usd-core", className: "widget-card panel-link" }, [
        el("div", { className: "panel-icon" }, ["💵"]),
        el("h3", { className: "widget-title" }, ["USD Core"]),
        el("p", { className: "widget-caption" }, [
          "DXY index, UUP ETF, and USDU broad dollar — the headline measures of dollar strength."
        ]),
        el("span", { className: "panel-cta" }, ["View Charts →"])
      ]),
      el("a", { href: "#/observatories/dollar_liquidity/fx-stress", className: "widget-card panel-link" }, [
        el("div", { className: "panel-icon" }, ["📉"]),
        el("h3", { className: "widget-title" }, ["FX Stress"]),
        el("p", { className: "widget-caption" }, [
          "USD/JPY, EUR/USD, and USD/CNH — pressure valves for global dollar demand."
        ]),
        el("span", { className: "panel-cta" }, ["View Charts →"])
      ]),
      el("a", { href: "#/observatories/dollar_liquidity/risk-credit", className: "widget-card panel-link" }, [
        el("div", { className: "panel-icon" }, ["⚠️"]),
        el("h3", { className: "widget-title" }, ["Risk & Credit"]),
        el("p", { className: "widget-caption" }, [
          "High yield credit, gold, VIX, and Treasury yields — risk sentiment and collateral dynamics."
        ]),
        el("span", { className: "panel-cta" }, ["View Charts →"])
      ])
    ])
  ]);
}

/**
 * Renders a chart card with title, description, and optional commentary stub
 */
function renderChartCard(options) {
  const { 
    id, 
    title, 
    description, 
    chartId, 
    size = "standard",
    showCommentary = false,
    liveIndicator = false
  } = options;
  
  const heightClass = {
    hero: "",
    standard: "",
    compact: "chart-sm",
    mini: "chart-xs"
  }[size] || "";
  
  return el("div", { className: "chart-card widget-card" }, [
    el("div", { className: "chart-card-header" }, [
      el("div", { className: "chart-card-title-row" }, [
        el("h3", { className: "widget-title" }, [title]),
        liveIndicator ? el("span", { className: "live-badge" }, [
          el("span", { className: "live-dot" }, []),
          "LIVE"
        ]) : null
      ].filter(Boolean)),
      el("p", { className: "widget-caption" }, [description])
    ]),
    el("div", { className: `chart-container ${heightClass}`, id: chartId }, [
      el("div", { className: "widget-loading" }, [
        el("span", { className: "loading-spinner" }, []),
        el("span", {}, ["Loading chart..."])
      ])
    ]),
    showCommentary ? el("div", { className: "chart-commentary" }, [
      el("div", { className: "commentary-header" }, [
        el("span", { className: "commentary-icon" }, ["◆"]),
        el("span", { className: "commentary-label" }, ["Signal Notes"]),
        el("span", { className: "coming-soon-badge" }, ["Coming Soon"])
      ]),
      el("p", { className: "commentary-placeholder" }, [
        "AI-generated market commentary and signal analysis will appear here."
      ])
    ]) : null
  ].filter(Boolean));
}

/**
 * Renders the methodology modal
 */
function renderMethodologyModal() {
  return el("div", { className: "modal-overlay", id: "methodology-modal" }, [
    el("div", { className: "modal-container" }, [
      el("div", { className: "modal-header" }, [
        el("h2", {}, ["Dollar Stress Index — Methodology"]),
        el("button", { className: "modal-close", id: "modal-close" }, ["×"])
      ]),
      el("div", { className: "modal-body" }, [
        el("section", { className: "modal-section" }, [
          el("h3", {}, ["Overview"]),
          el("p", {}, [
            "The ",
            el("strong", {}, ["Dollar Stress Index (DSI)"]),
            " is a proprietary composite indicator designed to quantify real-time stress in global USD funding markets. The index synthesizes signals across four key dimensions: dollar momentum, carry-adjusted FX pressure, volatility regime classification, and credit market dislocation."
          ]),
          el("p", {}, [
            "Unlike simple dollar strength measures, the DSI captures the ",
            el("em", {}, ["funding stress"]),
            " dynamic — the scramble for USD liquidity that occurs when global capital seeks safety in dollar-denominated assets, often at the expense of EM currencies, high-yield credit, and risk assets broadly."
          ])
        ]),
        
        el("section", { className: "modal-section" }, [
          el("h3", {}, ["Component Signals"]),
          
          el("div", { className: "signal-card" }, [
            el("h4", {}, [
              "DXY Momentum",
              el("span", { className: "signal-weight" }, ["30% weight"])
            ]),
            el("p", {}, [
              "Rate-of-change analysis on the DXY index using adaptive lookback windows (5d, 21d, 63d). The signal incorporates both level and acceleration components, with higher readings indicating rapid dollar appreciation — a hallmark of funding stress episodes."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h4", {}, [
              "JPY Carry Stress",
              el("span", { className: "signal-weight" }, ["25% weight"])
            ]),
            el("p", {}, [
              "USD/JPY adjusted for rate differentials and vol-carry. Monitors breakdowns in the yen carry trade, which historically precedes broader risk-off cascades. Incorporates BOJ intervention probability estimates."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h4", {}, [
              "Vol Regime",
              el("span", { className: "signal-weight" }, ["25% weight"])
            ]),
            el("p", {}, [
              "Multi-tenor VIX term structure analysis combined with realized/implied vol spreads. Classifies current regime as Low Vol, Transition, Elevated, or Crisis using hidden Markov model state probabilities."
            ])
          ]),
          
          el("div", { className: "signal-card" }, [
            el("h4", {}, [
              "Credit Z-Score",
              el("span", { className: "signal-weight" }, ["20% weight"])
            ]),
            el("p", {}, [
              "High-yield spread deviation from rolling mean, normalized by historical volatility. Tracks credit market liquidity via HYG/LQD ratio dynamics and CDX spread movements."
            ])
          ])
        ]),
        
        el("section", { className: "modal-section" }, [
          el("h3", {}, ["Interpretation Guide"]),
          el("div", { className: "interpretation-grid" }, [
            el("div", { className: "interp-item interp-low" }, [
              el("span", { className: "interp-range" }, ["0-24"]),
              el("span", { className: "interp-label" }, ["Low Stress"]),
              el("p", {}, ["Benign USD funding. Risk-on, carry trades performing."])
            ]),
            el("div", { className: "interp-item interp-moderate" }, [
              el("span", { className: "interp-range" }, ["25-44"]),
              el("span", { className: "interp-label" }, ["Moderate"]),
              el("p", {}, ["Normal fluctuations. No systemic pressure."])
            ]),
            el("div", { className: "interp-item interp-elevated" }, [
              el("span", { className: "interp-range" }, ["45-64"]),
              el("span", { className: "interp-label" }, ["Elevated"]),
              el("p", {}, ["Heightened vigilance. Early stress signs."])
            ]),
            el("div", { className: "interp-item interp-high" }, [
              el("span", { className: "interp-range" }, ["65-79"]),
              el("span", { className: "interp-label" }, ["High Stress"]),
              el("p", {}, ["Significant stress. Historical crisis analog."])
            ]),
            el("div", { className: "interp-item interp-extreme" }, [
              el("span", { className: "interp-range" }, ["80-100"]),
              el("span", { className: "interp-label" }, ["Extreme"]),
              el("p", {}, ["Crisis-level. Comparable to 2008/2020."])
            ])
          ])
        ]),
        
        el("section", { className: "modal-section" }, [
          el("p", { className: "disclaimer" }, [
            el("strong", {}, ["Not investment advice."]),
            " The Dollar Liquidity Observatory provides information for educational and research purposes only. Past performance does not guarantee future results."
          ])
        ])
      ])
    ])
  ]);
}

/**
 * Renders the complete Dollar Liquidity Observatory dashboard
 */
export function renderDollarLiquidityDashboard() {
  return el("div", { className: "dollar-liquidity-observatory" }, [
    renderObservatoryHeader(),
    renderTickerTape(),
    el("main", { className: "page-content" }, [
      el("section", { className: "section gauge-hero" }, [
        renderStressGaugeCard(),
        renderHeroText()
      ]),
      renderMarketSnapshot(),
      renderDashboardPanels()
    ]),
    renderMethodologyModal()
  ]);
}

// ============================================
// SUB-PAGE RENDERERS
// ============================================

/**
 * Renders USD Core sub-page with TradingView charts
 */
export function renderUSDCorePage() {
  return el("div", { className: "dollar-liquidity-observatory subpage" }, [
    renderObservatoryHeader("usd-core"),
    renderTickerTape(),
    el("main", { className: "page-content" }, [
      // Page Header
      el("section", { className: "section page-intro" }, [
        el("div", { className: "page-intro-content" }, [
          el("h1", { className: "page-title" }, ["USD Core Indicators"]),
          el("p", { className: "page-description" }, [
            "The spine of dollar liquidity monitoring: ",
            el("strong", {}, ["DXY"]),
            " (classic dollar index against major currencies), ",
            el("strong", {}, ["UUP"]),
            " (tradeable ETF with options liquidity), and ",
            el("strong", {}, ["USDU"]),
            " (broader basket with EM exposure)."
          ])
        ])
      ]),
      
      // DXY - Hero Chart
      el("section", { className: "section" }, [
        renderChartCard({
          id: "dxy",
          title: "DXY – U.S. Dollar Index",
          description: "The classic dollar index: 57.6% EUR, 13.6% JPY, 11.9% GBP, 9.1% CAD, 4.2% SEK, 3.6% CHF. Heavily weighted toward European currencies, making it sensitive to ECB policy divergence.",
          chartId: "chart-dxy",
          size: "hero",
          showCommentary: true,
          liveIndicator: true
        })
      ]),
      
      // UUP & USDU - Two Column
      el("section", { className: "section" }, [
        el("div", { className: "two-col-grid" }, [
          renderChartCard({
            id: "uup",
            title: "UUP – Dollar Bull ETF",
            description: "Futures-based ETF tracking DXY. Liquid options market for directional views or hedging. Useful for expressing dollar-bullish positions without FX exposure.",
            chartId: "chart-uup",
            size: "compact",
            liveIndicator: true
          }),
          renderChartCard({
            id: "usdu",
            title: "USDU – Broad Dollar",
            description: "Broader dollar basket including EM currencies. Better captures the global \"milkshake\" effect when dollars get scarce across emerging markets.",
            chartId: "chart-usdu",
            size: "compact",
            liveIndicator: true
          })
        ])
      ])
    ])
  ]);
}

/**
 * Renders FX Stress sub-page with TradingView charts
 */
export function renderFXStressPage() {
  return el("div", { className: "dollar-liquidity-observatory subpage" }, [
    renderObservatoryHeader("fx-stress"),
    renderTickerTape(),
    el("main", { className: "page-content" }, [
      // Page Header
      el("section", { className: "section page-intro" }, [
        el("div", { className: "page-intro-content" }, [
          el("h1", { className: "page-title" }, ["FX Stress Indicators"]),
          el("p", { className: "page-description" }, [
            "These three currency pairs are the main pressure valves for global dollar liquidity stress. ",
            el("strong", {}, ["USD/JPY"]),
            " reflects Japan's yield-curve control and carry trade dynamics. ",
            el("strong", {}, ["EUR/USD"]),
            " signals European funding pressure. ",
            el("strong", {}, ["USD/CNH"]),
            " captures China capital flows."
          ])
        ])
      ]),
      
      // USD/JPY - Hero Chart
      el("section", { className: "section" }, [
        renderChartCard({
          id: "usdjpy",
          title: "USD/JPY – Yen Stress",
          description: "When the yen weakens sharply against the dollar, it often signals dollar scarcity and carry trade unwinds. Watch for BOJ intervention levels (historically around 150+). Sharp moves here often precede broader risk-off.",
          chartId: "chart-usdjpy",
          size: "hero",
          showCommentary: true,
          liveIndicator: true
        })
      ]),
      
      // EUR/USD & USD/CNH
      el("section", { className: "section" }, [
        el("div", { className: "two-col-grid" }, [
          renderChartCard({
            id: "eurusd",
            title: "EUR/USD – Euro Stress",
            description: "Parity or sub-parity levels historically coincide with European fragility, energy shocks, and banking stress. The widening ECB-Fed rate differential drives medium-term trends.",
            chartId: "chart-eurusd",
            size: "compact",
            liveIndicator: true
          }),
          renderChartCard({
            id: "usdcnh",
            title: "USD/CNH – Offshore Yuan",
            description: "Reflects capital leaving China and global pressure around trade tensions and dollar availability. PBOC manages onshore rate tightly; offshore moves signal true stress.",
            chartId: "chart-usdcnh",
            size: "compact",
            liveIndicator: true
          })
        ])
      ]),
      
      // EM Extension
      el("section", { className: "section" }, [
        el("div", { className: "section-header section-divider" }, [
          el("h2", {}, ["EM FX Stress (Extended)"]),
          el("p", {}, ["High-beta emerging market currencies that show early signs of dollar funding stress."])
        ]),
        el("div", { className: "two-col-grid" }, [
          renderChartCard({
            id: "usdmxn",
            title: "USD/MXN – Mexican Peso",
            description: "High carry currency, sensitive to risk sentiment, nearshoring flows, and US-Mexico trade dynamics.",
            chartId: "chart-usdmxn",
            size: "compact"
          }),
          renderChartCard({
            id: "usdzar",
            title: "USD/ZAR – South African Rand",
            description: "Commodity-linked, high beta to global risk-off and dollar strength episodes. Often leads EM stress.",
            chartId: "chart-usdzar",
            size: "compact"
          })
        ])
      ])
    ])
  ]);
}

/**
 * Renders Risk & Credit sub-page with TradingView charts
 */
export function renderRiskCreditPage() {
  return el("div", { className: "dollar-liquidity-observatory subpage" }, [
    renderObservatoryHeader("risk-credit"),
    renderTickerTape(),
    el("main", { className: "page-content" }, [
      // Page Header
      el("section", { className: "section page-intro" }, [
        el("div", { className: "page-intro-content" }, [
          el("h1", { className: "page-title" }, ["Risk & Credit Indicators"]),
          el("p", { className: "page-description" }, [
            "These instruments capture risk sentiment, credit stress, and collateral dynamics. ",
            el("strong", {}, ["HYG/JNK"]),
            " track high yield credit spreads. ",
            el("strong", {}, ["Gold"]),
            " often moves with dollar stress. ",
            el("strong", {}, ["VIX"]),
            " spikes during liquidity crunches."
          ])
        ])
      ]),
      
      // Credit Spreads Section
      el("section", { className: "section" }, [
        el("div", { className: "section-header" }, [
          el("h2", {}, ["Credit Spreads"]),
          el("p", {}, ["High yield credit ETFs as proxies for corporate funding stress. Price declines signal spread widening."])
        ]),
        el("div", { className: "two-col-grid" }, [
          renderChartCard({
            id: "hyg",
            title: "HYG – iShares High Yield Corporate",
            description: "Most liquid high yield bond ETF. Price declines signal credit spread widening and funding stress. Watch for divergence from equities.",
            chartId: "chart-hyg",
            size: "compact",
            liveIndicator: true
          }),
          renderChartCard({
            id: "jnk",
            title: "JNK – SPDR Bloomberg High Yield",
            description: "Alternative high yield ETF. Useful for confirmation of HYG signals and monitoring credit market liquidity.",
            chartId: "chart-jnk",
            size: "compact",
            liveIndicator: true
          })
        ])
      ]),
      
      // Safe Haven & Volatility
      el("section", { className: "section" }, [
        el("div", { className: "section-header section-divider" }, [
          el("h2", {}, ["Safe Haven & Volatility"]),
          el("p", {}, ["Classic risk-off indicators and volatility measures."])
        ]),
        el("div", { className: "two-col-grid" }, [
          renderChartCard({
            id: "gold",
            title: "Gold (XAU/USD)",
            description: "Gold's relationship to dollar liquidity is nuanced. Can rally on debasement fears but sell off as collateral during acute stress (2008, March 2020).",
            chartId: "chart-gold",
            size: "compact",
            liveIndicator: true
          }),
          renderChartCard({
            id: "vix",
            title: "VIX – Volatility Index",
            description: "The \"fear gauge.\" VIX spikes above 30 often coincide with dollar funding stress. Term structure inversion (spot > futures) signals acute panic.",
            chartId: "chart-vix",
            size: "compact",
            liveIndicator: true
          })
        ])
      ]),
      
      // Treasury Yields
      el("section", { className: "section" }, [
        el("div", { className: "section-header section-divider" }, [
          el("h2", {}, ["Treasury Yields"]),
          el("p", {}, ["US government bond yields across the curve. The ultimate safe haven and funding benchmark."])
        ]),
        el("div", { className: "three-col-grid" }, [
          renderChartCard({
            id: "us2y",
            title: "US 2-Year Yield",
            description: "Fed policy expectations. Most sensitive to rate path changes.",
            chartId: "chart-us2y",
            size: "mini"
          }),
          renderChartCard({
            id: "us10y",
            title: "US 10-Year Yield",
            description: "Global risk-free benchmark. Key driver of mortgage rates and valuations.",
            chartId: "chart-us10y",
            size: "mini"
          }),
          renderChartCard({
            id: "us30y",
            title: "US 30-Year Yield",
            description: "Long-term expectations for growth and inflation.",
            chartId: "chart-us30y",
            size: "mini"
          })
        ])
      ]),
      
      // Yield Curve
      el("section", { className: "section" }, [
        renderChartCard({
          id: "curve",
          title: "10Y-2Y Spread (Yield Curve)",
          description: "Classic recession indicator. Inversion (negative spread) has preceded every US recession since 1970. Currently watching for re-steepening which can signal recession has begun.",
          chartId: "chart-curve",
          size: "standard",
          showCommentary: true
        })
      ])
    ])
  ]);
}

// ============================================
// UPDATE FUNCTIONS
// ============================================

/**
 * Updates the stress gauge display
 */
function updateGauge(data) {
  const needle = document.getElementById("gauge-needle");
  if (needle) {
    const rotation = -90 + (data.score / 100) * 180;
    needle.style.transform = `rotate(${rotation}deg)`;
  }
  
  const gaugeFill = document.querySelector(".gauge-fill");
  if (gaugeFill) {
    const arcLength = 251.2;
    const offset = arcLength - (data.score / 100) * arcLength;
    gaugeFill.style.strokeDashoffset = offset;
  }
  
  const scoreEl = document.getElementById("stress-score");
  if (scoreEl) scoreEl.textContent = data.score;
  
  const statusEl = document.getElementById("stress-status");
  if (statusEl) {
    const level = getStressLevel(data.score);
    statusEl.textContent = level.label;
    statusEl.className = `gauge-status ${level.class}`;
  }
  
  const components = ["dxy", "usdjpy", "vix", "hyg"];
  components.forEach(comp => {
    const fillEl = document.getElementById(`comp-${comp}`);
    const valEl = document.getElementById(`comp-${comp}-val`);
    if (fillEl && data.components[comp] !== undefined) {
      const val = data.components[comp];
      fillEl.style.width = `${val}%`;
      fillEl.className = `component-fill ${getComponentClass(val)}`;
    }
    if (valEl && data.components[comp] !== undefined) {
      valEl.textContent = data.components[comp];
    }
  });
}

/**
 * Updates market status indicator
 */
function updateMarketStatus() {
  const statusEl = document.getElementById("market-status");
  if (!statusEl) return;
  
  const textEl = statusEl.querySelector(".status-text");
  if (!textEl) return;
  
  const now = new Date();
  const etOptions = { timeZone: "America/New_York", hour: "numeric", minute: "numeric", hour12: false };
  const etTime = now.toLocaleString("en-US", etOptions);
  const parts = etTime.split(":");
  const etHour = parseInt(parts[0], 10);
  const etMinute = parseInt(parts[1], 10);
  const etTotalMinutes = etHour * 60 + etMinute;
  
  const dayOptions = { timeZone: "America/New_York", weekday: "short" };
  const etDay = now.toLocaleString("en-US", dayOptions);
  const isWeekend = etDay === "Sat" || etDay === "Sun";
  
  const preMarketOpen = 4 * 60;
  const marketOpen = 9 * 60 + 30;
  const marketClose = 16 * 60;
  const afterHoursClose = 20 * 60;
  
  statusEl.classList.remove("is-open", "is-closed", "is-premarket");
  
  if (isWeekend) {
    statusEl.classList.add("is-closed");
    const daysUntilMonday = etDay === "Sat" ? 2 : 1;
    const minutesUntilOpen = (daysUntilMonday * 24 * 60) - etTotalMinutes + marketOpen;
    const hoursUntil = Math.floor(minutesUntilOpen / 60);
    const minsUntil = minutesUntilOpen % 60;
    textEl.textContent = `Closed · Opens in ${hoursUntil}h ${minsUntil}m`;
  } else if (etTotalMinutes >= marketOpen && etTotalMinutes < marketClose) {
    statusEl.classList.add("is-open");
    const minutesUntilClose = marketClose - etTotalMinutes;
    const hoursUntil = Math.floor(minutesUntilClose / 60);
    const minsUntil = minutesUntilClose % 60;
    textEl.textContent = `US Markets Open · Closes in ${hoursUntil}h ${minsUntil}m`;
  } else if (etTotalMinutes >= preMarketOpen && etTotalMinutes < marketOpen) {
    statusEl.classList.add("is-premarket");
    const minutesUntilOpen = marketOpen - etTotalMinutes;
    const hoursUntil = Math.floor(minutesUntilOpen / 60);
    const minsUntil = minutesUntilOpen % 60;
    textEl.textContent = `Pre-Market · Opens in ${hoursUntil}h ${minsUntil}m`;
  } else if (etTotalMinutes >= marketClose && etTotalMinutes < afterHoursClose) {
    statusEl.classList.add("is-premarket");
    textEl.textContent = "After Hours";
  } else {
    statusEl.classList.add("is-closed");
    let minutesUntilPremarket;
    if (etTotalMinutes >= afterHoursClose) {
      minutesUntilPremarket = (24 * 60) - etTotalMinutes + preMarketOpen;
    } else {
      minutesUntilPremarket = preMarketOpen - etTotalMinutes;
    }
    const hoursUntil = Math.floor(minutesUntilPremarket / 60);
    const minsUntil = minutesUntilPremarket % 60;
    textEl.textContent = `Closed · Pre-market in ${hoursUntil}h ${minsUntil}m`;
  }
}

/**
 * Sets up modal functionality
 */
function setupModal() {
  const methodologyBtn = document.getElementById("methodology-btn");
  const modal = document.getElementById("methodology-modal");
  const closeBtn = document.getElementById("modal-close");
  
  if (!methodologyBtn || !modal) return;
  
  methodologyBtn.addEventListener("click", () => {
    modal.classList.add("is-visible");
    document.body.style.overflow = "hidden";
  });
  
  if (closeBtn) {
    closeBtn.addEventListener("click", () => {
      modal.classList.remove("is-visible");
      document.body.style.overflow = "";
    });
  }
  
  modal.addEventListener("click", (e) => {
    if (e.target === modal) {
      modal.classList.remove("is-visible");
      document.body.style.overflow = "";
    }
  });
  
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && modal.classList.contains("is-visible")) {
      modal.classList.remove("is-visible");
      document.body.style.overflow = "";
    }
  });
}

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initializes the Dollar Liquidity Observatory (Overview)
 */
export function initDollarLiquidityDashboard() {
  const gaugeContainer = document.querySelector(".gauge-container");
  if (gaugeContainer) {
    const scoreDiv = gaugeContainer.querySelector(".gauge-score");
    if (scoreDiv) {
      gaugeContainer.insertBefore(renderStressGaugeSVG(), scoreDiv);
    }
  }
  
  // Initial gauge update (registered for cleanup)
  registerTimeout(setTimeout(() => {
    const demoData = generateDemoData();
    updateGauge(demoData);
  }, 500));
  
  // Periodic gauge updates (registered for cleanup)
  registerInterval(setInterval(() => {
    const demoData = generateDemoData();
    updateGauge(demoData);
  }, 30000));
  
  // Market status updates (registered for cleanup)
  updateMarketStatus();
  registerInterval(setInterval(updateMarketStatus, 60000));
  
  setupModal();
  
  // Load widgets (registered for cleanup)
  registerTimeout(setTimeout(() => {
    loadTickerTape("ticker-tape");
    loadMarketOverview("market-overview");
  }, 100));
}

/**
 * Initializes USD Core sub-page charts
 */
export function initUSDCorePage() {
  updateMarketStatus();
  registerInterval(setInterval(updateMarketStatus, 60000));
  
  registerTimeout(setTimeout(() => {
    loadTickerTape("ticker-tape");
    loadAdvancedChart("chart-dxy", "TVC:DXY", CHART_HEIGHTS.hero);
    loadAdvancedChart("chart-uup", "AMEX:UUP", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-usdu", "AMEX:USDU", CHART_HEIGHTS.compact);
  }, 100));
}

/**
 * Initializes FX Stress sub-page charts
 */
export function initFXStressPage() {
  updateMarketStatus();
  registerInterval(setInterval(updateMarketStatus, 60000));
  
  registerTimeout(setTimeout(() => {
    loadTickerTape("ticker-tape");
    loadAdvancedChart("chart-usdjpy", "FX:USDJPY", CHART_HEIGHTS.hero);
    loadAdvancedChart("chart-eurusd", "FX:EURUSD", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-usdcnh", "FX:USDCNH", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-usdmxn", "FX:USDMXN", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-usdzar", "FX:USDZAR", CHART_HEIGHTS.compact);
  }, 100));
}

/**
 * Initializes Risk & Credit sub-page charts
 */
export function initRiskCreditPage() {
  updateMarketStatus();
  registerInterval(setInterval(updateMarketStatus, 60000));
  
  registerTimeout(setTimeout(() => {
    loadTickerTape("ticker-tape");
    loadAdvancedChart("chart-hyg", "AMEX:HYG", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-jnk", "AMEX:JNK", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-gold", "TVC:GOLD", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-vix", "CBOE:VIX", CHART_HEIGHTS.compact);
    loadAdvancedChart("chart-us2y", "TVC:US02Y", CHART_HEIGHTS.mini);
    loadAdvancedChart("chart-us10y", "TVC:US10Y", CHART_HEIGHTS.mini);
    loadAdvancedChart("chart-us30y", "TVC:US30Y", CHART_HEIGHTS.mini);
    loadAdvancedChart("chart-curve", "TVC:US10Y-TVC:US02Y", CHART_HEIGHTS.standard);
  }, 100));
}

export { generateDemoData, updateGauge, getStressLevel, loadAdvancedChart, loadTickerTape };
